import { useState, useEffect } from "react";
import { supabase } from "./db";

export default function AttributeManager() {
  const [managerTab, setManagerTab] = useState("categories"); // 'categories' alebo 'global'

  // Stavy pre Kategórie
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newCatName, setNewCatName] = useState("");
  const [newCatIcon, setNewCatIcon] = useState("📦");

  // Stavy pre Globálne sekcie
  const GLOBAL_SECTIONS = [
    "Základné identifikátory",
    "Produktový obsah a marketing",
    "Logistické údaje",
    "Obchodné podmienky",
  ];
  const [selectedGlobalSection, setSelectedGlobalSection] = useState(
    GLOBAL_SECTIONS[0]
  );

  // Spoločné stavy pre atribúty
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [newAttrName, setNewAttrName] = useState("");
  const [newAttrType, setNewAttrType] = useState("text");
  const [newAttrOptions, setNewAttrOptions] = useState("");
  const [newAttrRequired, setNewAttrRequired] = useState(false);

  const sanitizeColumnName = (name) => {
    return name
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
  };

  useEffect(() => {
    if (managerTab === "categories") {
      fetchCategories();
      if (selectedCategory)
        fetchAttributes(
          "category_attributes",
          "category_name",
          selectedCategory
        );
    } else {
      fetchAttributes(
        "global_attributes",
        "section_name",
        selectedGlobalSection
      );
    }
  }, [managerTab, selectedCategory, selectedGlobalSection]);

  const fetchCategories = async () => {
    const { data } = await supabase
      .from("categories")
      .select("*")
      .order("name");
    if (data) {
      setCategories(data);
      if (!selectedCategory && data.length > 0)
        setSelectedCategory(data[0].name);
    }
  };

  const fetchAttributes = async (table, filterKey, filterValue) => {
    setLoading(true);
    const { data } = await supabase
      .from(table)
      .select("*")
      .eq(filterKey, filterValue)
      .order("created_at");
    setAttributes(data || []);
    setLoading(false);
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCatName.trim()) return;
    await supabase
      .from("categories")
      .insert([{ name: newCatName.trim(), icon: newCatIcon }]);
    setNewCatName("");
    setNewCatIcon("📦");
    fetchCategories();
  };

  const handleDeleteCategory = async (id, name) => {
    if (!window.confirm(`Vymazať kategóriu "${name}"?`)) return;
    await supabase.from("categories").delete().eq("id", id);
    if (selectedCategory === name)
      setSelectedCategory(categories[0]?.name || null);
    fetchCategories();
  };

  const toggleRequired = async (id, currentStatus) => {
    const table =
      managerTab === "categories" ? "category_attributes" : "global_attributes";
    setAttributes((prev) =>
      prev.map((attr) =>
        attr.id === id ? { ...attr, is_required: !currentStatus } : attr
      )
    );
    await supabase
      .from(table)
      .update({ is_required: !currentStatus })
      .eq("id", id);
  };

  const handleDeleteAttribute = async (id, attrName) => {
    if (!window.confirm(`Vymazať filter? Zmaže sa aj stĺpec z databázy!`))
      return;
    const table =
      managerTab === "categories" ? "category_attributes" : "global_attributes";

    setAttributes((prev) => prev.filter((attr) => attr.id !== id));
    const { error } = await supabase.from(table).delete().eq("id", id);
    if (!error)
      await supabase.rpc("drop_product_column", {
        col_name: sanitizeColumnName(attrName),
      });
  };

  const handleAddAttribute = async (e) => {
    e.preventDefault();
    if (!newAttrName.trim()) return;

    const table =
      managerTab === "categories" ? "category_attributes" : "global_attributes";
    const filterKey =
      managerTab === "categories" ? "category_name" : "section_name";
    const filterValue =
      managerTab === "categories" ? selectedCategory : selectedGlobalSection;

    let optionsArray =
      newAttrType === "select"
        ? newAttrOptions
            .split(",")
            .map((opt) => opt.trim())
            .filter(Boolean)
        : [];

    const { error } = await supabase.from(table).insert([
      {
        [filterKey]: filterValue,
        attribute_name: newAttrName,
        attribute_type: newAttrType,
        is_required: newAttrRequired,
        options: optionsArray,
      },
    ]);

    if (!error) {
      await supabase.rpc("add_product_column", {
        col_name: sanitizeColumnName(newAttrName),
      });
      setNewAttrName("");
      setNewAttrOptions("");
      setNewAttrRequired(false);
      fetchAttributes(table, filterKey, filterValue);
    }
  };

  const activeSelection =
    managerTab === "categories" ? selectedCategory : selectedGlobalSection;

  return (
    <div style={styles.container}>
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        <button
          onClick={() => setManagerTab("categories")}
          style={{
            ...styles.topTab,
            background: managerTab === "categories" ? "#0f172a" : "white",
            color: managerTab === "categories" ? "white" : "#475569",
          }}
        >
          🏷️ Parametre Kategórií
        </button>
        <button
          onClick={() => setManagerTab("global")}
          style={{
            ...styles.topTab,
            background: managerTab === "global" ? "#0f172a" : "white",
            color: managerTab === "global" ? "white" : "#475569",
          }}
        >
          🌍 Globálne polia formulára
        </button>
      </div>

      <div style={styles.grid}>
        {/* ĽAVÝ STĹPEC */}
        <div style={styles.sidebar}>
          <h3 style={styles.sidebarTitle}>
            {managerTab === "categories" ? "Kategórie" : "Sekcie formulára"}
          </h3>

          <div style={styles.categoryList}>
            {managerTab === "categories"
              ? categories.map((cat) => (
                  <div
                    key={cat.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "4px",
                    }}
                  >
                    <button
                      onClick={() => setSelectedCategory(cat.name)}
                      style={{
                        ...styles.catBtn,
                        background:
                          selectedCategory === cat.name
                            ? "#eff6ff"
                            : "transparent",
                        color:
                          selectedCategory === cat.name ? "#2563eb" : "#475569",
                        flex: 1,
                      }}
                    >
                      {cat.icon} {cat.name}
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(cat.id, cat.name)}
                      style={styles.deleteCatBtn}
                    >
                      🗑️
                    </button>
                  </div>
                ))
              : GLOBAL_SECTIONS.map((sec) => (
                  <button
                    key={sec}
                    onClick={() => setSelectedGlobalSection(sec)}
                    style={{
                      ...styles.catBtn,
                      background:
                        selectedGlobalSection === sec
                          ? "#eff6ff"
                          : "transparent",
                      color:
                        selectedGlobalSection === sec ? "#2563eb" : "#475569",
                      marginBottom: "4px",
                      width: "100%",
                    }}
                  >
                    📁 {sec}
                  </button>
                ))}
          </div>

          {managerTab === "categories" && (
            <div style={styles.addCategoryBox}>
              <h4
                style={{
                  margin: "0 0 10px 0",
                  fontSize: "12px",
                  color: "#3b82f6",
                }}
              >
                + Nová kategória
              </h4>
              <form
                onSubmit={handleAddCategory}
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="text"
                    value={newCatIcon}
                    onChange={(e) => setNewCatIcon(e.target.value)}
                    style={{ ...styles.input, width: "45px" }}
                  />
                  <input
                    type="text"
                    placeholder="Názov kategórie"
                    required
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    style={{ ...styles.input, flex: 1 }}
                  />
                </div>
                <button type="submit" style={styles.addBtn}>
                  Pridať do zoznamu
                </button>
              </form>
            </div>
          )}
        </div>

        {/* PRAVÝ STĹPEC */}
        <div style={styles.mainContent}>
          <div style={styles.card}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "25px",
                borderBottom: "1px solid #e2e8f0",
                paddingBottom: "15px",
              }}
            >
              <h3 style={{ margin: 0, color: "#0f172a", fontSize: "20px" }}>
                Filtre pre:{" "}
                <span style={{ color: "#3b82f6" }}>{activeSelection}</span>
              </h3>
            </div>

            {loading ? (
              <p>Načítavam...</p>
            ) : (
              <div style={styles.attrList}>
                {attributes.map((attr) => (
                  <div key={attr.id} style={styles.attrRow}>
                    <div style={{ flex: 1 }}>
                      <strong
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          color: "#1e293b",
                          fontSize: "15px",
                          marginBottom: "4px",
                        }}
                      >
                        {attr.attribute_name}{" "}
                        {attr.is_required && (
                          <span style={{ color: "#dc2626" }}>*</span>
                        )}
                      </strong>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                          marginTop: "4px",
                        }}
                      >
                        <span
                          style={{
                            background: "white",
                            padding: "2px 8px",
                            borderRadius: "10px",
                            border: "1px solid #e2e8f0",
                            marginRight: "8px",
                          }}
                        >
                          Typ:{" "}
                          {attr.attribute_type === "select"
                            ? "Roletka"
                            : attr.attribute_type === "textarea"
                            ? "Dlhý text"
                            : "Text"}{" "}
                          {attr.attribute_type === "select" &&
                            ` (${attr.options.join(", ")})`}
                        </span>
                        <span
                          style={{
                            background: "#f1f5f9",
                            padding: "2px 8px",
                            borderRadius: "10px",
                            fontFamily: "monospace",
                          }}
                        >
                          Stĺpec: {sanitizeColumnName(attr.attribute_name)}
                        </span>
                      </div>
                    </div>
                    <div style={styles.actions}>
                      <button
                        onClick={() =>
                          toggleRequired(attr.id, attr.is_required)
                        }
                        style={{
                          ...styles.toggleBtn,
                          backgroundColor: attr.is_required
                            ? "#fef2f2"
                            : "#f1f5f9",
                          color: attr.is_required ? "#dc2626" : "#64748b",
                          border: attr.is_required
                            ? "1px solid #fecaca"
                            : "1px solid #e2e8f0",
                        }}
                      >
                        {attr.is_required ? "✅ Povinné" : "Nepovinné"}
                      </button>
                      <button
                        onClick={() =>
                          handleDeleteAttribute(attr.id, attr.attribute_name)
                        }
                        style={styles.deleteAttrBtn}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={styles.addBox}>
              <h4
                style={{
                  margin: "0 0 15px 0",
                  color: "#1e293b",
                  fontSize: "16px",
                }}
              >
                ➕ Pridať nové pole
              </h4>
              <form onSubmit={handleAddAttribute} style={styles.addForm}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "15px",
                  }}
                >
                  <div>
                    <label style={styles.label}>Názov poľa</label>
                    <input
                      type="text"
                      placeholder="Názov"
                      value={newAttrName}
                      onChange={(e) => setNewAttrName(e.target.value)}
                      required
                      style={styles.input}
                    />
                  </div>
                  <div>
                    <label style={styles.label}>Typ poľa</label>
                    <select
                      value={newAttrType}
                      onChange={(e) => setNewAttrType(e.target.value)}
                      style={styles.input}
                    >
                      <option value="text">Klasické textové pole</option>
                      <option value="textarea">
                        Viacriadkový text (Dlhý popis)
                      </option>
                      <option value="select">
                        Rozbaľovací zoznam (Roletka)
                      </option>
                    </select>
                  </div>
                </div>
                {newAttrType === "select" && (
                  <div>
                    <label style={styles.label}>
                      Možnosti (oddeľte čiarkou)
                    </label>
                    <input
                      type="text"
                      value={newAttrOptions}
                      onChange={(e) => setNewAttrOptions(e.target.value)}
                      required
                      style={styles.input}
                    />
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "5px",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "14px",
                      cursor: "pointer",
                      color: "#475569",
                      fontWeight: "600",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={newAttrRequired}
                      onChange={(e) => setNewAttrRequired(e.target.checked)}
                      style={{ width: "18px", height: "18px" }}
                    />
                    Označiť ako Povinné (*)
                  </label>
                  <button type="submit" style={styles.addBtn}>
                    + Vytvoriť
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: "100%",
    fontFamily: "'Inter', sans-serif",
    paddingBottom: "40px",
  },
  topTab: {
    padding: "10px 20px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    fontWeight: "bold",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  grid: {
    display: "flex",
    gap: "30px",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  sidebar: {
    width: "280px",
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    backdropFilter: "blur(20px)",
    padding: "20px 0",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 10px rgba(0,0,0,0.02)",
  },
  sidebarTitle: {
    margin: "0 0 15px 20px",
    fontSize: "12px",
    textTransform: "uppercase",
    color: "#94a3b8",
    fontWeight: "800",
  },
  categoryList: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "400px",
    overflowY: "auto",
    paddingRight: "10px",
  },
  catBtn: {
    textAlign: "left",
    padding: "10px 20px",
    borderRadius: "0 10px 10px 0",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
  },
  deleteCatBtn: {
    background: "none",
    border: "none",
    fontSize: "14px",
    cursor: "pointer",
    opacity: 0.5,
    padding: "5px",
    marginRight: "10px",
  },
  addCategoryBox: {
    margin: "20px 20px 0 20px",
    padding: "15px",
    backgroundColor: "#eff6ff",
    borderRadius: "12px",
    border: "1px dashed #bfdbfe",
  },
  mainContent: { flex: 1, minWidth: "300px" },
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(20px)",
    padding: "30px",
    borderRadius: "24px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 10px 30px -10px rgba(0,0,0,0.05)",
  },
  attrList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginBottom: "30px",
  },
  attrRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "15px 20px",
    backgroundColor: "#f8fafc",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
  },
  actions: { display: "flex", alignItems: "center", gap: "10px" },
  toggleBtn: {
    padding: "6px 12px",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "700",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  deleteAttrBtn: {
    background: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "6px",
    fontSize: "16px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
  },
  addBox: {
    backgroundColor: "#eff6ff",
    padding: "25px",
    borderRadius: "16px",
    border: "1px dashed #93c5fd",
  },
  addForm: { display: "flex", flexDirection: "column", gap: "15px" },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#1e40af",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #bfdbfe",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
  },
  addBtn: {
    padding: "12px 24px",
    backgroundColor: "#2563eb",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontWeight: "bold",
    cursor: "pointer",
  },
};
